import React, { useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { JWT_ABOUT } from '../content/toolGuides';
import { JWT_ROUTE } from '../content/tools';
import { decodeJwt, DecodedJwt, JwtError } from '../utils/jwt';
import './JwtDecoder.css';

const EXAMPLE_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9' +
  '.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ' +
  '.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';

/** The one-line verdict shown above the decoded segments. */
const statusOf = (decoded: DecodedJwt): { variant: string; text: string } => {
  if (decoded.isExpired) {
    return { variant: 'danger', text: 'Expired' };
  }
  if (decoded.isNotYetValid) {
    return { variant: 'warning', text: 'Not valid yet' };
  }
  if (decoded.isExpired === false) {
    return { variant: 'success', text: 'Within its validity window' };
  }
  return { variant: 'secondary', text: 'No expiry claim' };
};

const claimValue = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value);

const JwtDecoder: React.FC = () => {
  const [token, setToken] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Decoding is cheap and pure, so it runs on every keystroke rather than
  // behind a button: the verdict badge is the whole point of the page.
  const { decoded, error } = useMemo(() => {
    if (!token.trim()) {
      return { decoded: null, error: null };
    }
    try {
      return { decoded: decodeJwt(token), error: null };
    } catch (err) {
      return {
        decoded: null,
        error: err instanceof JwtError ? err.message : 'Could not decode that token.',
      };
    }
  }, [token]);

  const copy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const status = decoded ? statusOf(decoded) : null;

  return (
    <>
      <RouteSeo route={JWT_ROUTE} />
      <ToolLayout
        title="JWT Decoder"
        description="Decode a JSON Web Token and read its header, claims and expiry"
        icon={faShieldHalved}
        controls={
          <div className="tool-controls__group">
            <Button variant="link" size="sm" className="px-0" onClick={() => setToken(EXAMPLE_TOKEN)}>
              Load Example
            </Button>
            <Button variant="link" size="sm" className="px-0" onClick={() => setToken('')}>
              Clear
            </Button>
          </div>
        }
        error={error}
        inputLabel="JSON Web Token"
        inputValue={token}
        onInputChange={setToken}
        inputPlaceholder="Paste a JWT (header.payload.signature)..."
        inputRows={12}
        inputHint="Decoded in your browser. The signature is never verified and nothing is uploaded."
        outputContent={
          decoded ? (
            <div className="jwt-result">
              <div className="jwt-status">
                <Badge bg={status!.variant}>{status!.text}</Badge>
                {decoded.expiresAt && (
                  <span className="jwt-status__detail">
                    Expires {decoded.expiresAt.toLocaleString()}
                  </span>
                )}
              </div>

              <section className="jwt-segment">
                <h3 className="jwt-segment__title">
                  Header
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => copy(decoded.headerJson, 'header')}
                    aria-label="Copy decoded header"
                  >
                    <FontAwesomeIcon icon={copiedField === 'header' ? faCheck : faCopy} />
                  </Button>
                </h3>
                <pre className="code-block">
                  <code>{decoded.headerJson}</code>
                </pre>
              </section>

              <section className="jwt-segment">
                <h3 className="jwt-segment__title">
                  Payload
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => copy(decoded.payloadJson, 'payload')}
                    aria-label="Copy decoded payload"
                  >
                    <FontAwesomeIcon icon={copiedField === 'payload' ? faCheck : faCopy} />
                  </Button>
                </h3>
                <pre className="code-block">
                  <code>{decoded.payloadJson}</code>
                </pre>
              </section>

              {decoded.claims.length > 0 && (
                <section className="jwt-segment">
                  <h3 className="jwt-segment__title">Registered claims</h3>
                  <table className="table table-sm jwt-claims mb-0">
                    <thead>
                      <tr>
                        <th scope="col">Claim</th>
                        <th scope="col">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decoded.claims.map((claim) => (
                        <tr key={claim.name}>
                          <td>
                            <code>{claim.name}</code>
                            <span className="jwt-claims__description">{claim.description}</span>
                          </td>
                          <td>
                            <span className="jwt-claims__value">{claimValue(claim.value)}</span>
                            {claim.date && (
                              <span className="jwt-claims__description">
                                {claim.date.toLocaleString()}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              <section className="jwt-segment">
                <h3 className="jwt-segment__title">Signature</h3>
                <pre className="code-block jwt-signature">
                  <code>{decoded.signature}</code>
                </pre>
                <p className="jwt-note mb-0">
                  Not verified — checking it requires the issuer's key.
                </p>
              </section>
            </div>
          ) : (
            <p className="text-muted mb-0">
              The decoded header, claims and expiry will appear here as you paste.
            </p>
          )
        }
        aboutTitle="About JSON Web Tokens"
        about={JWT_ABOUT}
      >
        <ToolContent route={JWT_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default JwtDecoder;
